import L from 'leaflet'

// Leaflet with the circle marker extras every map uses: a text in the circle (stop numbers, P/D/R) and a checkered
// fill (the finish of a route). Import Leaflet from here instead of from 'leaflet' so the patch is always applied.

declare module 'leaflet' {
    interface CircleMarkerOptions {
        /** Drawn in the centre of the circle, in bold */
        text?: string | number
        /** Colour of the text, black by default */
        fontColor?: string
        /** Fill with a checker pattern instead of fillColor */
        checkered?: boolean
        /** Size of one checker cell in px, half the radius by default */
        checkeredSize?: number
        checkeredColor1?: string
        checkeredColor2?: string
    }
}

// The parts of Leaflet's canvas renderer and circle marker the patch uses. Not in @types/leaflet because they are
// private, but stable within Leaflet 1.x.
interface CanvasLayer {
    options: L.CircleMarkerOptions & {_dashArray?: number[]}
    _point?: L.Point
    _radius?: number
}

interface CanvasRendererInternals {
    _fillStroke: (ctx: CanvasRenderingContext2D, layer: CanvasLayer) => void
}

const checkerPatterns: Record<string, CanvasPattern> = {}

// Cached, so the pattern's tile canvas isn't rebuilt for every marker on every frame
function getCheckerPattern(ctx: CanvasRenderingContext2D, cellSize: number, color1: string, color2: string) {
    const key = `${cellSize}_${color1}_${color2}`

    if (!checkerPatterns[key]) {
        const tile = document.createElement('canvas')
        tile.width = cellSize * 2
        tile.height = cellSize * 2

        const tileCtx = tile.getContext('2d')
        tileCtx.fillStyle = color1
        tileCtx.fillRect(0, 0, cellSize, cellSize)
        tileCtx.fillRect(cellSize, cellSize, cellSize, cellSize)
        tileCtx.fillStyle = color2
        tileCtx.fillRect(cellSize, 0, cellSize, cellSize)
        tileCtx.fillRect(0, cellSize, cellSize, cellSize)

        checkerPatterns[key] = ctx.createPattern(tile, 'repeat')
    }

    return checkerPatterns[key]
}

/** Font size of a marker text: numbers up to 2 characters 10px, 3 characters 11px, longer texts 9px */
export function getMarkerFontSize(text: string | number) {
    const length = String(text).length

    return length <= 2 ? 10 : length === 3 ? 11 : 9
}

const renderer = L.Canvas.prototype as unknown as CanvasRendererInternals
const fillStroke = renderer._fillStroke

// Leaflet calls _fillStroke after it traced the shape's path (circle or polyline). Only the fill changes for a
// checkered circle and the text is drawn on top, so the rest of Leaflet's drawing stays its own.
renderer._fillStroke = function (ctx, layer) {
    const {options, _point: point} = layer

    if (options.checkered && point) {
        const radius = Math.max(Math.round(layer._radius), 1)
        const cellSize = options.checkeredSize || Math.max(Math.round(radius / 2), 2)
        const pattern = getCheckerPattern(ctx, cellSize, options.checkeredColor1 || '#000000', options.checkeredColor2 || '#ffffff')

        // Centre the point where the pattern's cells cross on the marker
        pattern.setTransform?.(new DOMMatrix().translate(point.x - cellSize, point.y - cellSize))

        ctx.globalAlpha = options.fillOpacity ?? 1
        ctx.fillStyle = pattern
        ctx.fill((options.fillRule || 'evenodd') as 'evenodd' | 'nonzero')

        fillStroke.call(this, ctx, {...layer, options: {...options, fill: false}})
    } else {
        fillStroke.call(this, ctx, layer)
    }

    if (point && options.text !== undefined && options.text !== null && options.text !== '') {
        ctx.globalAlpha = 1
        ctx.font = `bold ${getMarkerFontSize(options.text)}px arial`
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillStyle = options.fontColor || 'black'
        ctx.fillText(String(options.text), point.x, point.y + 1)
    }
}

export default L
